import { api, apiJson, getApiBase } from "./http.js";
import { saveTokenForGiveaway } from "../lib/wicoopProfileStorage.js";

function absMediaUrl(path) {
  if (!path) return "";
  const s = String(path).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const base = getApiBase();
  if (base) return `${base}${s.startsWith("/") ? s : `/${s}`}`;
  return s.startsWith("/") ? s : `/${s}`;
}

function mapGiveawayCard(g) {
  if (!g || typeof g !== "object") return null;
  // Полная цена числом — из subtitle («1 528 ₽» → 1528). Нужна, чтобы показать
  // цену ЗА ВЫЧЕТОМ кэшбека (как в Android), а не полный ценник.
  const priceDigits = String(g.subtitle || "").replace(/[^\d]/g, "");
  return {
    id: g.id,
    shortCode: g.short_code || g.shortCode || "",
    title: g.title || "",
    subtitle: g.subtitle || "",
    priceRub: priceDigits ? Number(priceDigits) : null,
    image: absMediaUrl(g.image),
    spotsLeft: g.spots_left != null ? Number(g.spots_left) : 0,
    cashbackPercent: g.cashback_percent != null ? Number(g.cashback_percent) : 0,
    acceptsNewSessions: g.accepts_new_sessions !== false,
    nextOpenDate: g.next_open_date || null,
  };
}

function mapGiveawayDetail(g) {
  if (!g) return null;
  const images = Array.isArray(g.images)
    ? g.images.map(absMediaUrl).filter(Boolean)
    : [];
  const main = absMediaUrl(g.image);
  if (main && !images.includes(main)) images.unshift(main);
  return {
    ...mapGiveawayCard(g),
    description: g.description || "",
    conditions: g.conditions || "",
    steps: Array.isArray(g.steps) ? g.steps : [],
    images,
    platformName: g.platform_name || g.platform?.name || "",
    activeKeyword: g.active_keyword || "",
    keywordStepIndex: g.keyword_step_index,
    keywordCohorts: g.keyword_cohorts,
    payoutCalendarDays: g.payout_calendar_days_after_approval,
  };
}

export async function fetchGiveaways() {
  const data = await apiJson("/api/giveaways/");
  const list = Array.isArray(data) ? data : data?.results || [];
  return list.map(mapGiveawayCard).filter(Boolean);
}

// «Скоро»: раздачи, которые прямо сейчас закрыты (места на сегодня уже разобрали),
// но по плану spots_schedule откроются в ближайшие дни. Бэкенд отдаёт их со
// scope=tomorrow (горизонт 7 дней) и полем next_open_date. Нативные приложения
// (iOS/Android) показывают их во вкладке «Скоро»; Mini App этого не делала — из-за
// чего в мини-аппе было видно «только то, что открыто сейчас» (например одни щётки),
// а в приложениях — «много товаров». Показываем «Скоро» и здесь, чтобы витрины совпали.
export async function fetchGiveawaysUpcoming() {
  const data = await apiJson("/api/giveaways/?scope=tomorrow");
  const list = Array.isArray(data) ? data : data?.results || [];
  return list.map(mapGiveawayCard).filter(Boolean);
}

export async function fetchGiveawayById(id, verificationToken) {
  let path = `/api/giveaways/${encodeURIComponent(id)}/`;
  if (verificationToken) {
    path += `?token=${encodeURIComponent(verificationToken)}`;
  }
  const data = await apiJson(path);
  return mapGiveawayDetail(data);
}

export async function fetchGiveawayByShort(code) {
  const data = await apiJson(`/api/giveaways/by-short/${encodeURIComponent(code)}/`);
  return mapGiveawayDetail(data);
}

export async function fetchVitrineStories() {
  const data = await apiJson("/api/vitrine/stories/");
  const list = Array.isArray(data) ? data : data?.results || [];
  return list.map((s) => ({
    id: s.id,
    title: s.title || "",
    coverUrl: absMediaUrl(s.cover_url || s.cover),
    contentUrl: absMediaUrl(s.content_url || s.content),
    contentKind: s.content_kind || (String(s.content_url || "").match(/\.(mp4|webm)/i) ? "video" : "image"),
  }));
}

export async function fetchSbpBanks() {
  return apiJson("/api/sbp-banks/");
}

export async function fetchAccountMe() {
  return apiJson("/api/account/me/");
}

export async function patchAccountMe(payload) {
  return apiJson("/api/account/me/", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function fetchAccountParticipationsMapped() {
  const rows = await apiJson("/api/account/participations/");
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    giveawayId: row.giveaway_id,
    verificationToken: row.verification_token,
    giveaway: {
      id: row.giveaway_id,
      title: row.giveaway_title || "",
      image: absMediaUrl(row.giveaway_image),
    },
    participation: row.participation || {},
  }));
}

export async function fetchMyParticipation(giveawayId, verificationToken) {
  const path = `/api/giveaways/${encodeURIComponent(giveawayId)}/my-participation/?token=${encodeURIComponent(verificationToken || "")}`;
  const res = await api(path);
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 404) return { participates: false };
    throw new Error(data?.detail || "Ошибка загрузки участия");
  }
  if (!data || data.participates === false) return { participates: false };
  const p = data.participation && typeof data.participation === "object" ? data.participation : data;
  return {
    participates: true,
    can_continue: p.can_continue !== false,
    session_status: p.session_status,
    status_title: p.status_title,
    status_subtitle: p.status_subtitle,
    assigned_keyword: p.assigned_keyword,
    current_step_index: p.current_step_index,
    ...p,
  };
}

export async function registerGiveawayParticipant(giveawayId, body) {
  const data = await apiJson(`/api/giveaways/${encodeURIComponent(giveawayId)}/registration/`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const token = data.verification_token || data.token;
  if (token) saveTokenForGiveaway(giveawayId, token);
  return data;
}

export async function giveawayPhoneAuthInit(giveawayId, phone, email) {
  const raw = pinBodyFromArgs(phone, email, null, ["phone", "email"]);
  return apiJson(`/api/giveaways/${encodeURIComponent(giveawayId)}/phone-auth/init/`, {
    method: "POST",
    body: JSON.stringify({ phone: raw.phone, email: raw.email }),
  });
}

export async function giveawayPhoneAuthVerify(giveawayId, phone, email, code) {
  const raw = pinBodyFromArgs(phone, email, code, ["phone", "email", "code"]);
  return apiJson(`/api/giveaways/${encodeURIComponent(giveawayId)}/phone-auth/verify/`, {
    method: "POST",
    body: JSON.stringify({ phone: raw.phone, email: raw.email, code: raw.code }),
  });
}

export async function reportParticipationProgress(giveawayId, verificationToken, currentStepIndex) {
  return apiJson(`/api/giveaways/${encodeURIComponent(giveawayId)}/participation/progress/`, {
    method: "POST",
    body: JSON.stringify({ token: verificationToken, current_step_index: currentStepIndex }),
  });
}

export async function submitParticipationComplete(giveawayId, verificationToken) {
  return apiJson(`/api/giveaways/${encodeURIComponent(giveawayId)}/submit-participation/`, {
    method: "POST",
    body: JSON.stringify({ token: verificationToken }),
  });
}

export async function fetchStepScreenshots(giveawayId, verificationToken) {
  const path = `/api/giveaways/${encodeURIComponent(giveawayId)}/step-screenshots/?token=${encodeURIComponent(verificationToken)}`;
  const data = await apiJson(path);
  if (Array.isArray(data)) return { screenshots: data };
  return { screenshots: data?.screenshots ?? data?.results ?? [] };
}

export async function uploadStepScreenshot(giveawayId, verificationToken, stepIndex, file) {
  const form = new FormData();
  form.append("token", verificationToken);
  form.append("step_index", String(stepIndex));
  form.append("file", file);
  const res = await api(`/api/giveaways/${encodeURIComponent(giveawayId)}/step-screenshots/`, {
    method: "POST",
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.detail || "Не удалось загрузить скриншот");
  return data;
}

export async function deleteStepScreenshot(giveawayId, pk, verificationToken) {
  const path = `/api/giveaways/${encodeURIComponent(giveawayId)}/step-screenshots/${pk}/?token=${encodeURIComponent(verificationToken)}`;
  const res = await api(path, { method: "DELETE" });
  if (!res.ok && res.status !== 204) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail || "Не удалось удалить скриншот");
  }
}

export async function marketplaceTelegramWebappBindPhone(phone) {
  return apiJson("/api/account/telegram-webapp/bind-phone/", {
    method: "POST",
    body: JSON.stringify({ phone }),
  });
}

export async function marketplaceMaxWebappBindPhone(phone) {
  return apiJson("/api/account/max-webapp/bind-phone/", {
    method: "POST",
    body: JSON.stringify({ phone }),
  });
}

function pinBodyFromArgs(first, second, third, keys) {
  if (first && typeof first === "object" && !Array.isArray(first)) {
    return { ...first };
  }
  const body = {};
  if (keys[0] !== undefined) body[keys[0]] = first;
  if (keys[1] !== undefined && second !== undefined) body[keys[1]] = second;
  if (keys[2] !== undefined && third !== undefined) body[keys[2]] = third;
  return body;
}

export async function marketplacePinHasPin(phone) {
  const body = pinBodyFromArgs(phone, null, null, ["phone"]);
  return apiJson("/api/account/pin/has-pin/", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function marketplacePinRequestOtp(phone, email) {
  const raw = pinBodyFromArgs(phone, email, null, ["phone", "email"]);
  const body = { phone: raw.phone };
  if (raw.email) body.email = raw.email;
  return apiJson("/api/account/pin/request-otp/", { method: "POST", body: JSON.stringify(body) });
}

export async function marketplacePinVerifyOtp(phone, code, email) {
  const raw = pinBodyFromArgs(phone, code, email, ["phone", "code", "email"]);
  const body = { phone: raw.phone, code: raw.code };
  if (raw.email) body.email = raw.email;
  return apiJson("/api/account/pin/verify-otp/", { method: "POST", body: JSON.stringify(body) });
}

export async function marketplacePinSet(otpToken, pin, email) {
  const raw = pinBodyFromArgs(otpToken, pin, email, ["otp_token", "pin", "email"]);
  const body = { otp_token: raw.otp_token, pin: raw.pin };
  if (raw.email) body.email = raw.email;
  return apiJson("/api/account/pin/set/", { method: "POST", body: JSON.stringify(body) });
}

export async function marketplacePinLogin(phone, pin, email) {
  const raw = pinBodyFromArgs(phone, pin, email, ["phone", "pin", "email"]);
  const body = { phone: raw.phone, pin: raw.pin };
  if (raw.email) body.email = raw.email;
  return apiJson("/api/account/pin/login/", { method: "POST", body: JSON.stringify(body) });
}

export async function marketplacePinResetRequestOtp(phone, email) {
  const raw = pinBodyFromArgs(phone, email, null, ["phone", "email"]);
  const body = { phone: raw.phone };
  if (raw.email) body.email = raw.email;
  return apiJson("/api/account/pin/reset/request-otp/", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function marketplacePinResetConfirm(phone, code, pin) {
  const raw = pinBodyFromArgs(phone, code, pin, ["phone", "code", "pin"]);
  const body = {
    phone: raw.phone,
    code: raw.code,
    pin: raw.new_pin ?? raw.pin,
  };
  return apiJson("/api/account/pin/reset/confirm/", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function marketplacePasskeyRegisterBegin(phone) {
  const body = pinBodyFromArgs(phone, null, null, ["phone"]);
  return apiJson("/api/account/passkey/register/begin/", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function marketplacePasskeyRegisterFinish(phone, credential) {
  const raw = pinBodyFromArgs(phone, credential, null, ["phone", "credential"]);
  const body = {
    phone: raw.phone,
    token: raw.token,
    credential: raw.credential,
  };
  if (raw.label) body.label = raw.label;
  return apiJson("/api/account/passkey/register/finish/", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function marketplacePasskeyLoginBegin(phone) {
  const raw = pinBodyFromArgs(phone ?? {}, null, null, ["phone"]);
  const body = raw.phone ? { phone: raw.phone } : {};
  return apiJson("/api/account/passkey/login/begin/", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function marketplacePasskeyLoginFinish(credential, token) {
  const raw = pinBodyFromArgs(credential, token, null, ["credential", "token"]);
  const body = {
    token: raw.token,
    credential: raw.credential,
  };
  if (raw.phone) body.phone = raw.phone;
  if (raw.email) body.email = raw.email;
  return apiJson("/api/account/passkey/login/finish/", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function formatStepBodyForDisplay(body, keyword) {
  const b = String(body || "");
  if (!/\{ключевое\s+слово\}/i.test(b) && !/\{keyword\}/i.test(b)) return b;
  const k = keyword || "";
  return b.replace(/\{ключевое слово\}/gi, k).replace(/\{keyword\}/gi, k);
}

export function resolveKeywordForStep(stepIndex, giveaway, assignedKeyword) {
  const kw = assignedKeyword || giveaway?.activeKeyword || giveaway?.active_keyword || "";
  const idx = Number(stepIndex);
  const cohorts = giveaway?.keywordCohorts || giveaway?.keyword_cohorts;
  if (Array.isArray(cohorts)) {
    for (const c of cohorts) {
      if (Number(c?.step_index) === idx && c?.keyword) return String(c.keyword);
    }
  }
  const kwIdx = giveaway?.keywordStepIndex ?? giveaway?.keyword_step_index;
  if (kwIdx != null && Number(kwIdx) === idx && kw) return kw;
  return kw;
}

export async function fetchHelpArticles() {
  const data = await apiJson("/api/vitrine/help-articles/");
  return Array.isArray(data) ? data : data?.results || [];
}
